import React, { createContext, useContext, useState, useCallback } from 'react';
import arTranslations from '../locales/ar.json';
import enTranslations from '../locales/en.json';

// ─── Translation loader ───────────────────────────────────────────────────────
// This function is designed to be easily swapped to an API fetch in the future.
// When the admin CMS is ready, replace this with:
//   const response = await fetch(`/api/translations/${lang}`);
//   return response.json();
const loadTranslations = async (lang) => {
  const map = { ar: arTranslations, en: enTranslations };
  return map[lang] || arTranslations;
};

// ─── Context ──────────────────────────────────────────────────────────────────
const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('orkina_lang') || 'ar';
  });
  const [translations, setTranslations] = useState(
    lang === 'ar' ? arTranslations : enTranslations
  );
  const [loading, setLoading] = useState(false);

  const switchLang = useCallback(async (newLang) => {
    setLoading(true);
    try {
      const t = await loadTranslations(newLang);
      setTranslations(t);
      setLang(newLang);
      localStorage.setItem('orkina_lang', newLang);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleLang = useCallback(() => {
    switchLang(lang === 'ar' ? 'en' : 'ar');
  }, [lang, switchLang]);

  // Resolve dot-notation key (e.g. 'hero.slides')
  const t = useCallback(
    (key) => {
      const keys = key.split('.');
      let result = translations;
      for (const k of keys) {
        if (result === undefined || result === null) return key;
        result = result[k];
      }
      return result !== undefined ? result : key;
    },
    [translations]
  );

  const isRTL = lang === 'ar';

  return (
    <LanguageContext.Provider value={{ lang, isRTL, t, toggleLang, loading }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within a LanguageProvider');
  return ctx;
};

export default LanguageContext;
